/* Payroll run state machine, WPS export, payslips. */
import { normIban } from "../../domain/compliance";
import { evPayrollApproved, evPayslips } from "./events";
import { overtimeHoursForPeriod } from "../../domain/biometric";
import { defaultAdj, payslip, validateAdjustment } from "../../domain/payroll";
import type * as T from "../types";
import { E, fmtMoney, fmtNum, isNum, type Ctx } from "./ctx";
import { effAdj, loansSorted, parsePeriod, periodRows, putFile, runView } from "./derived";

/** Go encoding/csv field quoting rules. */
export function csvField(f: string): string {
  if (f === "") return f;
  const needs = f === "\\." || /[,"\r\n]/.test(f) || /^\s/u.test(f);
  return needs ? '"' + f.replace(/"/g, '""') + '"' : f;
}
export const csvRows = (rows: string[][]) => rows.map((r) => r.map(csvField).join(",") + "\r\n").join("");

export function wpsCsv(emps: T.Employee[], slips: T.Payslip[]): string {
  const byId = new Map(emps.map((e) => [e.id, e]));
  const rows = [["EmployeeCode", "Name", "IdNumber", "Bank", "IBAN", "Basic", "Housing", "Other", "Deductions", "Net"]];
  for (const s of slips) {
    const e = byId.get(s.employeeId)!;
    rows.push([e.code, e.nameEn, e.idNumber, e.bank, normIban(e.iban), fmtMoney(s.basic), fmtMoney(s.housing), fmtMoney(s.earnings - s.basic - s.housing), fmtMoney(s.deductions), fmtMoney(s.net)]);
  }
  return csvRows(rows);
}

export function payrollOps(c: Ctx) {
  const run = () => c.state.run;
  return {
    async payrollHistory(): Promise<T.PayrollHistoryItem[]> {
      c.principal("payroll.read");
      return c.state.history.slice().sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    },

    async getPayrollRun(): Promise<T.PayrollRunView> {
      c.principal("payroll.read");
      return runView(c);
    },

    async setAdjustment(employeeId: number, adj: T.Adjustment): Promise<T.PayrollRunView> {
      const u = c.principal("payroll.ops");
      if (adj == null || typeof adj !== "object") throw E.badRequest("JSON object expected");
      const fe = validateAdjustment(adj);
      if (Object.keys(fe).length) throw E.validation(fe);
      c.emp(employeeId);
      if (run().approved) throw E.conflict("payroll run is approved");
      const a: T.Adjustment = { ot: isNum(adj.ot) ? adj.ot : 0, bonus: isNum(adj.bonus) ? adj.bonus : 0, absDays: isNum(adj.absDays) ? adj.absDays : 0, ded: isNum(adj.ded) ? adj.ded : 0 };
      run().adjustments[String(employeeId)] = a;
      c.audit(u, "Set payroll adjustment", "PayrollRun", run().id, `employee ${employeeId}: ot ${fmtNum(a.ot)}, bonus ${fmtNum(a.bonus)}, absDays ${fmtNum(a.absDays)}, ded ${fmtNum(a.ded)}`);
      return runView(c);
    },

    async importOvertimeFromAttendance(): Promise<T.PayrollRunView> {
      const u = c.principal("payroll.ops");
      const r = run();
      if (r.approved) throw E.conflict("payroll run is approved");
      const act = c.active();
      const rows = periodRows(c, r.period, act.map((e) => e.id));
      for (const e of act) r.adjustments[String(e.id)] = { ...effAdj(c, e), ot: overtimeHoursForPeriod(rows, e.id, r.period) };
      c.audit(u, "Imported overtime from attendance", "PayrollRun", r.id, `${act.length} employees`);
      return runView(c);
    },

    async setRunStep(step: number): Promise<T.PayrollRunView> {
      const u = c.principal("payroll.ops");
      if (!Number.isInteger(step) || step < 0 || step > 4) throw E.field("step", "must be an integer between 0 and 4");
      const r = run();
      if (step > 2 && !r.approved) throw E.conflict("payroll run must be approved first");
      if (step > 3 && !r.exported) throw E.conflict("WPS file must be exported first");
      r.step = step;
      c.audit(u, "Moved payroll step", "PayrollRun", r.id, String(step));
      return runView(c);
    },

    async approveRun(): Promise<T.PayrollRunView> {
      const u = c.principal("payroll.approve");
      const v = runView(c);
      const r = run();
      if (r.approved) throw E.conflict("payroll run already approved");
      if (v.art92.length) throw E.conflict("Art. 92 cap exceeded");
      r.approved = true;
      r.approvedBy = u.name;
      r.approvedAt = c.now;
      r.step = Math.max(r.step, 3);
      c.audit(u, "Approved payroll", "PayrollRun", r.id, "net " + c.cur + " " + fmtMoney(v.totals.net));
      const out = runView(c);
      evPayrollApproved(c, out, u.name);
      return out;
    },

    async exportWps(): Promise<T.PayrollRunView> {
      const u = c.principal("payroll.ops");
      const v = runView(c);
      const r = run();
      if (!r.approved) throw E.conflict("payroll run must be approved first");
      const ref = putFile(c, "exports", "WPS-" + r.period + ".csv", "text/csv", wpsCsv(c.employeesSorted(), v.slips));
      r.exported = true;
      r.exportFile = ref;
      r.step = Math.max(r.step, 3);
      c.audit(u, "Exported WPS file", "PayrollRun", r.id, ref.key);
      return runView(c);
    },

    async ackMudad(): Promise<T.PayrollRunView> {
      const u = c.principal("payroll.ops");
      const r = run();
      if (!r.exported) throw E.conflict("WPS file must be exported first");
      r.mudadAck = true;
      c.audit(u, "Acknowledged Mudad", "PayrollRun", r.id, "");
      return runView(c);
    },

    async markPaid(): Promise<T.PayrollRunView> {
      const u = c.principal("payroll.ops");
      const v = runView(c);
      const r = run();
      if (!r.exported || !r.mudadAck) throw E.conflict("WPS export and Mudad acknowledgement are required");
      if (r.paid) throw E.conflict("payroll run already paid");
      r.paid = true;
      r.step = 4;
      const item: T.PayrollHistoryItem = {
        id: r.period, year: Number(r.period.slice(0, 4)), month: Number(r.period.slice(5, 7)), gross: Math.round(v.totals.gross),
        employees: v.totals.headcount, status: "Paid", payDate: r.period + "-" + String(c.state.settings.payDay).padStart(2, "0"),
      };
      const i = c.state.history.findIndex((h) => h.id === item.id);
      if (i >= 0) c.state.history[i] = item; else c.state.history.push(item);
      c.audit(u, "Marked payroll paid", "PayrollRun", r.id, "gross " + c.cur + " " + fmtMoney(v.totals.gross));
      evPayslips(c, r.period, item.payDate, v.slips);
      return runView(c);
    },

    async getPayslip(employeeId: number, period: string): Promise<T.Payslip> {
      const u = c.principal("payroll.payslip");
      parsePeriod(period);
      if (c.isEmployee(u) && c.ownEid(u) !== employeeId) throw E.forbidden("employees may only view their own payslip");
      const e = c.emp(employeeId);
      let adj: T.Adjustment = { ot: 0, bonus: 0, absDays: 0, ded: 0 };
      if (period === run().period) adj = effAdj(c, e);
      else if (employeeId % 2 === 1) adj = { ...adj, ot: defaultAdj(e).ot };
      return payslip(e, adj, loansSorted(c), c.state.settings.gosi, c.today, period, c.country);
    },
  };
}
